import React, { useEffect, useMemo } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Spin, Tag, Tooltip, Typography } from 'antd';

import type {
  InspectAssessmentCandidatePosition,
  InspectAssessmentCandidatePositionEvidence,
  InspectAssessmentOptionChoiceInstrument,
} from '../../../../state/app/inspect_assessment/types';
import { formatAssessmentDate } from './formatters';

type Props = {
  evidence?: InspectAssessmentCandidatePositionEvidence;
  error?: string;
  fetched: boolean;
  fetching: boolean;
  hasSelectedAttempt: boolean;
  metric?: ExposureMetric;
  onLoad: () => void;
};

type ExposureMetric =
  | 'portfolio-fit'
  | 'position-direction'
  | 'position-size'
  | 'option-choice';

type ScoreComponentKey =
  | 'risk_vector_fit'
  | 'directional_alignment'
  | 'conviction_to_sizing_fit'
  | 'convexity_option_choice';

type MetricConfig = {
  componentKey: ScoreComponentKey;
  description: string;
  label: string;
  maximumPoints: number;
  periodIndexLabel: string;
  pointColumnLabel?: string;
  workspaceLabel: string;
};

type InstrumentScoreDetail = {
  credit: number;
  pointContribution: number;
  weight: number;
};

const SCORE_EPSILON = 1e-9;

const METRIC_CONFIG: Record<ExposureMetric, MetricConfig> = {
  'portfolio-fit': {
    componentKey: 'risk_vector_fit',
    description:
      'The engine divides each exposure by that instrument’s limit, then compares the whole user vector with the optimal vector. The resulting fit supplies 35% of Pillar 2.',
    label: 'Overall fit',
    maximumPoints: 35,
    periodIndexLabel: 'Overall portfolio fit period index',
    workspaceLabel: 'Overall portfolio fit workspace',
  },
  'position-direction': {
    componentKey: 'directional_alignment',
    description:
      'The engine checks whether each user position is on the same side as its optimal target, then exposure-weights those results. Position direction supplies 20% of Pillar 2.',
    label: 'Position direction',
    maximumPoints: 20,
    periodIndexLabel: 'Position direction period index',
    pointColumnLabel: 'Direction points',
    workspaceLabel: 'Position direction workspace',
  },
  'position-size': {
    componentKey: 'conviction_to_sizing_fit',
    description:
      'The engine ignores direction, compares each user position’s absolute limit usage with its optimal target, then exposure-weights those results. Position size supplies 20% of Pillar 2.',
    label: 'Position size',
    maximumPoints: 20,
    periodIndexLabel: 'Position size period index',
    pointColumnLabel: 'Size points',
    workspaceLabel: 'Position size workspace',
  },
  'option-choice': {
    componentKey: 'convexity_option_choice',
    description:
      'The engine scores only option instruments. It compares the user’s absolute limit usage with the optimal option size, then applies the option’s scored-PCS signal gate. Option choice supplies 10% of Pillar 2.',
    label: 'Option choice',
    maximumPoints: 10,
    periodIndexLabel: 'Option choice period index',
    workspaceLabel: 'Option choice workspace',
  },
};

const formatNumber = (value: number, maximumFractionDigits = 2) =>
  value.toLocaleString('en', {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  });

const formatSigned = (value: number) =>
  `${value > 0 ? '+' : ''}${formatNumber(value)}`;

const formatUtilization = (value: number) =>
  `${value > 0 ? '+' : ''}${formatNumber(100 * value, 1)}%`;

const formatMagnitudeUtilization = (value: number) =>
  `${formatNumber(100 * Math.abs(value), 1)}%`;

const formatComponentPoints = (score: number | null, maximumPoints: number) =>
  score === null ? '—' : (maximumPoints * score).toFixed(1);

const exposureText = (row: InspectAssessmentCandidatePosition) =>
  `${formatSigned(row.candidate_position)} ${row.unit}`;

const targetText = (row: InspectAssessmentCandidatePosition) =>
  `${formatSigned(row.optimal_target)} ${row.unit}`;

const barStyle = (value: number, scale: number): React.CSSProperties => {
  const width = `${Math.min(50, (50 * Math.abs(value)) / scale)}%`;
  return value < 0 ? { right: '50%', width } : { left: '50%', width };
};

const magnitudeBarStyle = (
  value: number,
  scale: number,
): React.CSSProperties => ({
  left: 0,
  width: `${Math.min(100, (100 * Math.abs(value)) / scale)}%`,
});

const clampUnit = (value: number) => Math.max(0, Math.min(1, value));

const positionDirectionTerm = (row: InspectAssessmentCandidatePosition) => {
  const candidate = row.candidate_utilization;
  const optimal = row.optimal_utilization;
  const candidateIsFlat = Math.abs(candidate) <= SCORE_EPSILON;
  const optimalIsFlat = Math.abs(optimal) <= SCORE_EPSILON;
  let credit: number;

  if (candidateIsFlat && optimalIsFlat) credit = 1;
  else if (optimalIsFlat) credit = clampUnit(1 - Math.abs(candidate) / 0.5);
  else if (candidateIsFlat) credit = clampUnit(1 - Math.abs(optimal) / 0.5);
  else if (Math.sign(candidate) === Math.sign(optimal)) credit = 1;
  else credit = 0;

  return {
    credit,
    weight: Math.max(Math.abs(candidate), Math.abs(optimal), 0.05),
  };
};

const positionSizeTerm = (row: InspectAssessmentCandidatePosition) => {
  const candidate = Math.abs(row.candidate_utilization);
  const optimal = Math.abs(row.optimal_utilization);
  const anchoredCandidate = candidate + 0.03;
  const anchoredOptimal = optimal + 0.03;

  return {
    credit: Math.exp(-Math.abs(Math.log(anchoredCandidate / anchoredOptimal))),
    weight: Math.max(candidate, optimal, 0.05),
  };
};

const FitVectorRow: React.FC<{
  scoreDetail?: InstrumentScoreDetail;
  row: InspectAssessmentCandidatePosition;
  scale: number;
}> = ({ row, scale, scoreDetail }) => (
  <div
    className={`assessment-inspect-assessment__fit-row${
      scoreDetail ? ' assessment-inspect-assessment__fit-row--with-points' : ''
    }`}
  >
    <div className="assessment-inspect-assessment__fit-instrument">
      <strong>{row.instrument_code}</strong>
      <span>{row.unit}</span>
    </div>
    <div
      aria-label={`${row.instrument_code}: user ${exposureText(
        row,
      )}, optimal ${targetText(row)}`}
      className="assessment-inspect-assessment__fit-bars"
      role="img"
    >
      <span className="assessment-inspect-assessment__fit-zero" />
      <div className="assessment-inspect-assessment__fit-bar-track">
        <span
          className="assessment-inspect-assessment__fit-bar assessment-inspect-assessment__fit-bar--candidate"
          style={barStyle(row.candidate_utilization, scale)}
        />
      </div>
      <div className="assessment-inspect-assessment__fit-bar-track">
        <span
          className="assessment-inspect-assessment__fit-bar assessment-inspect-assessment__fit-bar--optimal"
          style={barStyle(row.optimal_utilization, scale)}
        />
      </div>
    </div>
    <div className="assessment-inspect-assessment__fit-value">
      <strong>{exposureText(row)}</strong>
      <span>{formatUtilization(row.candidate_utilization)} of limit</span>
    </div>
    <div className="assessment-inspect-assessment__fit-value">
      <strong>{targetText(row)}</strong>
      <span>{formatUtilization(row.optimal_utilization)} of limit</span>
    </div>
    <div className="assessment-inspect-assessment__fit-value">
      <strong>{formatNumber(row.soft_limit)}</strong>
      <span>{row.unit}</span>
    </div>
    {scoreDetail && (
      <div className="assessment-inspect-assessment__fit-value assessment-inspect-assessment__fit-points">
        <strong>{scoreDetail.pointContribution.toFixed(2)} pts</strong>
        <span>
          {formatNumber(100 * scoreDetail.credit, 1)}% credit · weight{' '}
          {formatNumber(scoreDetail.weight, 2)}
        </span>
      </div>
    )}
  </div>
);

const optionRuleLabel = (
  rule: InspectAssessmentOptionChoiceInstrument['rule'],
) => {
  if (rule === 'both_flat') return 'Both flat';
  if (rule === 'optimal_uses_option') return 'Expected by optimal';
  return 'Not expected by optimal';
};

const OptionChoiceRow: React.FC<{
  row: InspectAssessmentOptionChoiceInstrument;
  scale: number;
}> = ({ row, scale }) => {
  const userHasOption = row.candidate_utilization > SCORE_EPSILON;
  const signalLabel = row.signal_valid
    ? 'Valid signal'
    : userHasOption
      ? 'Invalid signal'
      : 'No user option';
  const requirement =
    row.signal_requirement === 'greater_than'
      ? `> +${formatNumber(row.activation_threshold, 1)}`
      : `< -${formatNumber(row.activation_threshold, 1)}`;

  return (
    <div className="assessment-inspect-assessment__fit-row assessment-inspect-assessment__fit-row--option">
      <div className="assessment-inspect-assessment__fit-instrument">
        <strong>{row.instrument_code}</strong>
        <span>
          Parent: {row.parent_code || 'not recorded'} · benefits if{' '}
          {row.parent_code || 'parent'} {row.payoff_direction}
        </span>
      </div>
      <div
        aria-label={`${row.instrument_code}: user option ${formatMagnitudeUtilization(
          row.candidate_utilization,
        )} of limit, optimal option ${formatMagnitudeUtilization(
          row.optimal_utilization,
        )} of limit`}
        className="assessment-inspect-assessment__fit-bars assessment-inspect-assessment__fit-bars--magnitude"
        role="img"
      >
        <div className="assessment-inspect-assessment__fit-bar-track">
          <span
            className="assessment-inspect-assessment__fit-bar assessment-inspect-assessment__fit-bar--candidate"
            style={magnitudeBarStyle(row.candidate_utilization, scale)}
          />
        </div>
        <div className="assessment-inspect-assessment__fit-bar-track">
          <span
            className="assessment-inspect-assessment__fit-bar assessment-inspect-assessment__fit-bar--optimal"
            style={magnitudeBarStyle(row.optimal_utilization, scale)}
          />
        </div>
      </div>
      <div className="assessment-inspect-assessment__fit-value">
        <strong>
          {formatSigned(row.candidate_position)} {row.unit}
        </strong>
        <span>
          {formatMagnitudeUtilization(row.candidate_utilization)} of limit
        </span>
        {Math.abs(row.option_premium_m) > SCORE_EPSILON && (
          <Tooltip title="Diagnostic only: Option choice does not read option premium directly.">
            <span
              aria-label="About candidate option premium"
              className="assessment-inspect-assessment__fit-option-premium"
              tabIndex={0}
            >
              Premium ${formatNumber(row.option_premium_m)}m{' '}
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        )}
      </div>
      <div className="assessment-inspect-assessment__fit-value">
        <strong>
          {formatSigned(row.optimal_target)} {row.unit}
        </strong>
        <span>
          {formatMagnitudeUtilization(row.optimal_utilization)} of limit
        </span>
        <span>{optionRuleLabel(row.rule)}</span>
      </div>
      <div className="assessment-inspect-assessment__fit-value">
        <strong>{formatNumber(row.soft_limit)}</strong>
        <span>{row.unit}</span>
      </div>
      <div className="assessment-inspect-assessment__fit-value assessment-inspect-assessment__fit-option-signal">
        <strong>{formatSigned(row.benchmark_pcs)}</strong>
        <span>Requires {requirement}</span>
        <span
          className={
            row.signal_valid || !userHasOption
              ? 'assessment-inspect-assessment__fit-option-signal--valid'
              : 'assessment-inspect-assessment__fit-option-signal--invalid'
          }
        >
          {signalLabel} · ×{row.signal_multiplier.toFixed(2)}
        </span>
      </div>
      <div className="assessment-inspect-assessment__fit-value assessment-inspect-assessment__fit-points">
        <strong>{row.point_contribution.toFixed(2)} pts</strong>
        <span>
          {formatNumber(100 * row.base_credit, 1)}% base · ×
          {row.signal_multiplier.toFixed(2)} ={' '}
          {formatNumber(100 * row.final_credit, 1)}%
        </span>
        <span>weight {formatNumber(row.weight, 2)}</span>
      </div>
    </div>
  );
};

const OverallPortfolioFitWorkspace: React.FC<Props> = ({
  evidence,
  error,
  fetched,
  fetching,
  hasSelectedAttempt,
  metric = 'portfolio-fit',
  onLoad,
}) => {
  const metricConfig = METRIC_CONFIG[metric];
  const isOptionChoice = metric === 'option-choice';
  const hasInstrumentPoints = Boolean(metricConfig.pointColumnLabel);
  const optionAudit = evidence?.option_choice;
  const optionAuditUnavailable =
    isOptionChoice && (!optionAudit || optionAudit.status === 'unavailable');
  const provenance = isOptionChoice ? optionAudit : evidence;
  const detailsRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = React.useRef(new Map<number, HTMLElement>());
  const scrollFrameRef = React.useRef<number>();
  const [activePeriodIndex, setActivePeriodIndex] = React.useState<
    number | undefined
  >();

  useEffect(() => {
    if (hasSelectedAttempt && !fetched && !fetching && !error) onLoad();
  }, [error, fetched, fetching, hasSelectedAttempt, onLoad]);

  const periods = useMemo(() => {
    const grouped = new Map<number, InspectAssessmentCandidatePosition[]>();
    (evidence?.positions ?? []).forEach(row => {
      const values = grouped.get(row.period_index) ?? [];
      values.push(row);
      grouped.set(row.period_index, values);
    });
    return Array.from(grouped.entries())
      .sort(([left], [right]) => left - right)
      .map(([periodIndex, rows]) => {
        const sortedRows = rows.sort(
          (left, right) => left.vector_index - right.vector_index,
        );
        const reconciliation = evidence?.reconciliation.periods.find(
          period => period.period_index === periodIndex,
        );
        const optionPeriod = optionAudit?.periods.find(
          period => period.period_index === periodIndex,
        );

        return {
          score:
            reconciliation?.components[metricConfig.componentKey].stored ??
            null,
          label: sortedRows[0]?.period_label || `Period ${periodIndex}`,
          periodIndex,
          reconciliation,
          optionPeriod,
          rows: sortedRows,
        };
      });
  }, [evidence, metricConfig.componentKey, optionAudit]);

  useEffect(() => {
    if (
      activePeriodIndex === undefined ||
      !periods.some(period => period.periodIndex === activePeriodIndex)
    ) {
      setActivePeriodIndex(periods[0]?.periodIndex);
    }
  }, [activePeriodIndex, periods]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    [],
  );

  const scrollToPeriod = (periodIndex: number) => {
    const details = detailsRef.current;
    const section = sectionRefs.current.get(periodIndex);
    if (!details || !section) return;
    setActivePeriodIndex(periodIndex);
    details.scrollTo({
      behavior: 'smooth',
      top: Math.max(0, section.offsetTop - 6),
    });
  };

  const syncActivePeriod = () => {
    if (scrollFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const details = detailsRef.current;
      if (!details) return;
      const threshold = details.scrollTop + 16;
      let nextPeriodIndex = periods[0]?.periodIndex;
      for (const period of periods) {
        const section = sectionRefs.current.get(period.periodIndex);
        if (!section || section.offsetTop > threshold) break;
        nextPeriodIndex = period.periodIndex;
      }
      setActivePeriodIndex(nextPeriodIndex);
    });
  };

  if (!hasSelectedAttempt) {
    return (
      <section
        aria-label={metricConfig.workspaceLabel}
        className="assessment-inspect-assessment__pillar-one-workspace assessment-inspect-assessment__fit-state"
      >
        <Typography.Text type="secondary">
          Select a user and session attempt to inspect its scored exposure
          vector.
        </Typography.Text>
      </section>
    );
  }

  if (error) {
    return (
      <section
        aria-label={metricConfig.workspaceLabel}
        className="assessment-inspect-assessment__pillar-one-workspace assessment-inspect-assessment__fit-state"
      >
        <Alert
          action={
            <Button onClick={onLoad} size="small">
              Retry
            </Button>
          }
          description={error}
          showIcon
          title="Candidate Position evidence is unavailable"
          type="warning"
        />
      </section>
    );
  }

  if (fetching || !fetched || !evidence) {
    return (
      <section
        aria-label={metricConfig.workspaceLabel}
        className="assessment-inspect-assessment__pillar-one-workspace assessment-inspect-assessment__fit-state"
      >
        <Spin size="small" />
        <Typography.Text type="secondary">
          Loading the scored exposure vector…
        </Typography.Text>
      </section>
    );
  }

  return (
    <section
      aria-label={metricConfig.workspaceLabel}
      className="assessment-inspect-assessment__pillar-one-workspace assessment-inspect-assessment__fit-workspace"
    >
      <header className="assessment-inspect-assessment__fit-heading">
        <Typography.Text type="secondary">
          {metricConfig.description}
        </Typography.Text>
        <Tag
          color={
            optionAuditUnavailable
              ? 'red'
              : provenance?.exact
                ? 'green'
                : 'gold'
          }
        >
          {provenance?.source_label ?? 'Option breakdown unavailable'}
          {provenance && !provenance.exact && (
            <Tooltip
              title={
                <>
                  <strong>
                    {optionAuditUnavailable
                      ? 'Verified breakdown unavailable'
                      : 'Verified reconstruction, not an original snapshot'}
                  </strong>
                  <br />
                  {provenance.historical_note}
                  {optionAuditUnavailable &&
                    optionAudit?.unavailable_reason && (
                      <>
                        <br />
                        {optionAudit.unavailable_reason}
                      </>
                    )}
                </>
              }
            >
              <InfoCircleOutlined
                aria-label={
                  optionAuditUnavailable
                    ? 'About unavailable Option choice evidence'
                    : 'About reconstructed Candidate Position evidence'
                }
                className="assessment-inspect-assessment__fit-source-help"
                tabIndex={0}
              />
            </Tooltip>
          )}
        </Tag>
      </header>

      {provenance?.exact && (
        <Alert
          className="assessment-inspect-assessment__fit-provenance"
          description={provenance.historical_note}
          title="Original scoring evidence"
          showIcon
          type="success"
        />
      )}

      <div className="assessment-inspect-assessment__pillar-one-audit assessment-inspect-assessment__fit-audit">
        <nav
          aria-label={metricConfig.periodIndexLabel}
          className="assessment-inspect-assessment__pillar-one-period-index"
        >
          {periods.map(period => (
            <button
              aria-current={
                activePeriodIndex === period.periodIndex
                  ? 'location'
                  : undefined
              }
              className={`assessment-inspect-assessment__pillar-one-period-link ${
                activePeriodIndex === period.periodIndex
                  ? 'assessment-inspect-assessment__pillar-one-period-link--active'
                  : ''
              }`}
              key={period.periodIndex}
              onClick={() => scrollToPeriod(period.periodIndex)}
              onFocus={() => scrollToPeriod(period.periodIndex)}
              onMouseEnter={() => scrollToPeriod(period.periodIndex)}
              type="button"
            >
              <span>{period.label}</span>
              <span className="assessment-inspect-assessment__pillar-one-period-score">
                {formatComponentPoints(
                  period.score,
                  metricConfig.maximumPoints,
                )}
                <small>/{metricConfig.maximumPoints}</small>
              </span>
            </button>
          ))}
        </nav>

        <div
          className="assessment-inspect-assessment__fit-body"
          onScroll={syncActivePeriod}
          ref={detailsRef}
        >
          <div className="assessment-inspect-assessment__fit-legend">
            <span>
              <i className="assessment-inspect-assessment__fit-swatch assessment-inspect-assessment__fit-swatch--candidate" />
              User Candidate Position
            </span>
            <span>
              <i className="assessment-inspect-assessment__fit-swatch assessment-inspect-assessment__fit-swatch--optimal" />
              Optimal Target
            </span>
            <Typography.Text type="secondary">
              {isOptionChoice
                ? 'Bars start at zero and show absolute limit use because Option choice ignores position sign. Candidate Position is derived by the assessment adapter; PlayerPositionHistory.reporting_value is not substituted for it.'
                : 'Candidate Position is derived by the assessment adapter; PlayerPositionHistory.reporting_value is not substituted for it.'}
            </Typography.Text>
          </div>

          {optionAuditUnavailable && (
            <Alert
              description={
                optionAudit?.unavailable_reason ??
                'This response does not contain a verified Option choice breakdown.'
              }
              showIcon
              title="Per-option detail withheld"
              type="warning"
            />
          )}

          {periods.map(
            ({
              label,
              optionPeriod,
              periodIndex,
              reconciliation,
              rows,
              score,
            }) => {
              const scale = Math.max(
                1,
                ...(isOptionChoice
                  ? (optionPeriod?.options ?? []).flatMap(row => [
                      row.candidate_utilization,
                      row.optimal_utilization,
                    ])
                  : rows.flatMap(row => [
                      Math.abs(row.candidate_utilization),
                      Math.abs(row.optimal_utilization),
                    ])),
              );
              const scoreTerms = rows.map(row => ({
                row,
                ...(metric === 'position-size'
                  ? positionSizeTerm(row)
                  : positionDirectionTerm(row)),
              }));
              const totalWeight = scoreTerms.reduce(
                (total, term) => total + term.weight,
                0,
              );

              return (
                <article
                  className="assessment-inspect-assessment__fit-period"
                  key={periodIndex}
                  ref={node => {
                    if (node) sectionRefs.current.set(periodIndex, node);
                    else sectionRefs.current.delete(periodIndex);
                  }}
                >
                  <div className="assessment-inspect-assessment__fit-period-heading">
                    <Typography.Text strong>{label}</Typography.Text>
                    <Tag
                      color={
                        (
                          isOptionChoice
                            ? optionPeriod?.matches
                            : reconciliation?.matches
                        )
                          ? 'blue'
                          : 'red'
                      }
                    >
                      {metricConfig.label}{' '}
                      {score === null
                        ? '—'
                        : `${formatNumber(100 * score, 1)}/100`}
                      {' · '}
                      {score === null
                        ? '—'
                        : `${formatNumber(
                            metricConfig.maximumPoints * score,
                            1,
                          )}/${metricConfig.maximumPoints} pts`}
                    </Tag>
                  </div>
                  <div
                    aria-label={`${label} exposure comparison; scroll horizontally to inspect every value`}
                    className="assessment-inspect-assessment__fit-scroll"
                    tabIndex={0}
                  >
                    {isOptionChoice ? (
                      optionAuditUnavailable ? (
                        <div className="assessment-inspect-assessment__fit-option-empty">
                          Per-option values are not displayed without a
                          score-reconciled breakdown.
                        </div>
                      ) : optionPeriod?.options.length ? (
                        <div className="assessment-inspect-assessment__fit-vector assessment-inspect-assessment__fit-vector--option">
                          <div className="assessment-inspect-assessment__fit-columns assessment-inspect-assessment__fit-columns--option">
                            <span>Option</span>
                            <span>
                              <i>0</i>
                              <i>{formatNumber(100 * scale, 0)}%</i>
                            </span>
                            <span>User option</span>
                            <span>Optimal option</span>
                            <span>Limit</span>
                            <span>
                              Scored PCS{' '}
                              <Tooltip title="The option row’s Benchmark PCS value read by the scorer.">
                                <InfoCircleOutlined
                                  aria-label="About scored PCS"
                                  tabIndex={0}
                                />
                              </Tooltip>
                            </span>
                            <span>Option points</span>
                          </div>
                          {optionPeriod.options.map(row => (
                            <OptionChoiceRow
                              key={`${periodIndex}:${row.vector_index}`}
                              row={row}
                              scale={scale}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="assessment-inspect-assessment__fit-option-empty">
                          {optionAudit?.exact ? (
                            <>
                              <strong>No scored option instruments.</strong> The
                              engine awards this period 10/10.
                            </>
                          ) : (
                            <>
                              <strong>
                                No options identified in this reconstructed
                                breakdown.
                              </strong>{' '}
                              The saved Option choice score is 10/10.
                            </>
                          )}
                        </div>
                      )
                    ) : (
                      <div
                        className={`assessment-inspect-assessment__fit-vector${
                          hasInstrumentPoints
                            ? ' assessment-inspect-assessment__fit-vector--with-points'
                            : ''
                        }`}
                      >
                        <div
                          className={`assessment-inspect-assessment__fit-columns${
                            hasInstrumentPoints
                              ? ' assessment-inspect-assessment__fit-columns--with-points'
                              : ''
                          }`}
                        >
                          <span>Instrument</span>
                          <span>
                            <i>-{formatNumber(100 * scale, 0)}%</i>
                            <i>0</i>
                            <i>+{formatNumber(100 * scale, 0)}%</i>
                          </span>
                          <span>User exposure</span>
                          <span>Optimal exposure</span>
                          <span>Limit</span>
                          {metricConfig.pointColumnLabel && (
                            <span>{metricConfig.pointColumnLabel}</span>
                          )}
                        </div>
                        {scoreTerms.map(term => (
                          <FitVectorRow
                            scoreDetail={
                              hasInstrumentPoints
                                ? {
                                    credit: term.credit,
                                    pointContribution:
                                      totalWeight <= SCORE_EPSILON
                                        ? 0
                                        : (metricConfig.maximumPoints *
                                            term.credit *
                                            term.weight) /
                                          totalWeight,
                                    weight: term.weight,
                                  }
                                : undefined
                            }
                            key={`${term.row.period_index}:${term.row.vector_index}`}
                            row={term.row}
                            scale={scale}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            },
          )}
        </div>
      </div>

      <footer className="assessment-inspect-assessment__fit-footer">
        <Typography.Text type="secondary">
          Result #{evidence.result_id} · model {evidence.result_model_version} ·
          scored {formatAssessmentDate(evidence.result_computed_at)} ·{' '}
          {evidence.persisted !== false && evidence.created_at
            ? `evidence saved ${formatAssessmentDate(evidence.created_at)}`
            : 'evidence not saved (read-only database)'}{' '}
          · schema v{evidence.schema_version} · SHA-256{' '}
          {evidence.payload_sha256}
        </Typography.Text>
      </footer>
    </section>
  );
};

export default OverallPortfolioFitWorkspace;
